import { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient.js';

// Public route (/reset-password). Supabase processes the recovery token from
// the URL automatically and fires onAuthStateChange('PASSWORD_RECOVERY').
// No auth guard — the user is unauthenticated when they land here.
export default function ResetPassword() {
  const [phase,   setPhase]   = useState('verifying'); // 'verifying' | 'ready' | 'expired'
  const [pwd,     setPwd]     = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showCfm, setShowCfm] = useState(false);
  const [err,     setErr]     = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let done = false;
    const recoveryHash = typeof window !== 'undefined' && /type=recovery|access_token/.test(window.location.hash);
    const finish = () => { if (!done) { done = true; setPhase('ready'); } };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') finish();
    });
    // Covers the case where the event fired before this listener subscribed.
    if (recoveryHash) supabase.auth.getSession().then(({ data: { session } }) => { if (session) finish(); });
    const t = setTimeout(() => { if (!done) setPhase('expired'); }, 10000);
    return () => { subscription.unsubscribe(); clearTimeout(t); };
  }, []);

  const handleSet = async () => {
    setErr('');
    if (pwd.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pwd !== confirm) { setErr('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;
      window.location.replace('/');
    } catch {
      setErr('Something went wrong. Try again.');
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: '12px 44px 12px 14px', borderRadius: 10, background: 'var(--bg-elevated)',
    border: '1px solid var(--border-medium)', color: 'var(--text-primary)', fontSize: 14,
    outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
  };
  const toggle = (on, set) => (
    <button type="button" onClick={() => set(!on)} aria-label={on ? 'Hide password' : 'Show password'}
      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--text-muted)', padding: 4 }}>
      {on ? '🙈' : '👁️'}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', width: '100vw', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 440, margin: '0 24px', background: 'var(--bg-card)', border: '1px solid var(--border-soft)', borderRadius: 24, padding: '44px 40px', boxShadow: 'var(--shadow-card)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <img src="/logo.jpg" alt="AdaptOS" style={{ height: 90, width: 'auto', objectFit: 'contain' }} />
        </div>

        {phase === 'verifying' && (
          <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)', margin: '8px 0' }}>Verifying link…</p>
        )}

        {phase === 'expired' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' }}>Link expired or invalid</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 22px', lineHeight: 1.55 }}>This reset link has expired or isn&rsquo;t valid. Request a new one.</p>
            <a href="/signin" style={{ color: 'var(--accent-primary)', textDecoration: 'underline', fontWeight: 600, fontSize: 13 }}>← Back to sign in</a>
          </div>
        )}

        {phase === 'ready' && (
          <>
            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 24px' }}>Set a new password for your account.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>New password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPwd ? 'text' : 'password'} value={pwd} onChange={e => setPwd(e.target.value)} placeholder="At least 8 characters" style={inputStyle} />
                  {toggle(showPwd, setShowPwd)}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Confirm new password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showCfm ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSet()} placeholder="Re-enter password" style={inputStyle} />
                  {toggle(showCfm, setShowCfm)}
                </div>
              </div>
            </div>
            {err && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: 'var(--brand-coral)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>{err}</div>
            )}
            <button onClick={handleSet} disabled={loading} style={{
              width: '100%', padding: '13px 24px', borderRadius: 12, background: 'var(--brand-amber)', border: 'none',
              color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1,
            }}>{loading ? 'Saving…' : 'Set New Password'}</button>
          </>
        )}
      </div>
    </div>
  );
}
